'use client';

/**
 * Mirror of the shadcn Calendar in `apps/web/src/components/ui/calendar.tsx`.
 *
 * A MIRROR rather than a move, for the reasons spelled out in `popover.tsx`:
 * that directory is shadcn-managed and must not be edited, and a re-export
 * shim there would be clobbered by the next `shadcn add`. Identical to the
 * app's copy apart from the import paths and the day styling (`day`,
 * `day_button`, `selected`, `range_middle`) — if shadcn regenerates the app's
 * copy, re-apply the same diff here.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../lib/utils';
import { buttonVariants } from './button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        month_caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 top-1 size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 top-1 size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        // No `[&:has([aria-selected])]` rules here: those were written for rdp
        // v8, which put aria-selected on the button. Since v9 it is on this
        // cell, so they never matched.
        day: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
        // Hover never paints a fill over the cell. Since rdp v9 the selection
        // colours land on the day CELL, not this button — so the ghost
        // variant's grey hover fill painted a light rounded square over a dark
        // range end (its corners poking out), and vanished altogether on the
        // grey middle of a range, leaving no sign of which day the pointer was
        // on. An inset ring marks the hovered day on light cells; the dark
        // cells (see `selected`) lighten instead, since a rounded ring inside a
        // square dark cell leaves the same poking-out corners.
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 font-normal hover:bg-transparent hover:text-inherit hover:ring-1 hover:ring-primary hover:ring-inset dark:hover:bg-transparent',
        ),
        range_start:
          'day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground',
        range_end: 'day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground',
        selected:
          'bg-primary text-primary-foreground [&:not(.day-range-middle)>button:hover]:ring-transparent [&:not(.day-range-middle):has(>button:hover)]:bg-primary/80',
        today: 'bg-accent text-accent-foreground',
        outside: 'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        disabled: 'text-muted-foreground opacity-50',
        range_middle:
          'day-range-middle aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          ),
      }}
      {...props}
    />
  );
}

export { Calendar };
